'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { AuthCard } from '@/components/auth/layout/auth-card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { BrandIcons } from '@/components/vemmerui/brand-icons'
import { authClient, signIn } from '@/lib/auth/client'
import { getAbsoluteUrl, getUrlWithParams } from '@/utils/url'
import { ConfirmEmailAddress, type MagicLinkParams } from './confirm-email'

const SSignInFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})
const SSignupFormSchema = SSignInFormSchema.extend({
  firstName: z.string().trim().min(1, 'Field is required'),
  lastName: z.string().trim().min(1, 'Field is required'),
})

type TSignInFormValues = z.infer<typeof SSignInFormSchema>
type TSignUpFormValues = z.infer<typeof SSignupFormSchema>

interface AuthFormProps {
  className?: string
  mode: 'signin' | 'signup'
  title: string
  description: string
  footerText: string
  footerLinkText: string
  footerLinkHref: string
}
export function AuthForm({
  className,
  mode,
  title,
  description,
  footerText,
  footerLinkText,
  footerLinkHref,
}: AuthFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [magicLinkParams, setMagicLinkParams] = useState<MagicLinkParams | null>(null)
  const router = useRouter()
  const searchParams = useSearchParams()

  // We need to explicitly choose which form to create based on mode
  const isSignUp = mode === 'signup'

  const signInForm = useForm<TSignInFormValues>({
    resolver: zodResolver(SSignInFormSchema),
    defaultValues: { email: searchParams.get('email') || '' },
  })
  const signUpForm = useForm<TSignUpFormValues>({
    resolver: zodResolver(SSignupFormSchema),
    defaultValues: { email: searchParams.get('email') || '', firstName: '', lastName: '' },
  })

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true)
      const returnUrl = searchParams.get('returnUrl')
      await signIn.social({
        provider: 'google',
        callbackURL: getAbsoluteUrl(returnUrl || '/app'),
      })
    } catch (error) {
      setIsGoogleLoading(false)
      console.error('Google sign in failed:', error)
      if (error instanceof Error) {
        toast.error(error.message || 'Failed to sign in with Google')
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  // Create separate submit handlers for sign-in and sign-up
  const onSignInSubmit = async (data: TSignInFormValues) => {
    try {
      setIsLoading(true)
      const returnUrlPath = searchParams.get('returnUrl') || '/app'
      const returnUrl = getAbsoluteUrl(returnUrlPath)

      const params: MagicLinkParams = {
        email: data.email,
        callbackURL: returnUrl,
        fetchOptions: {
          body: { mode: 'signin' },
        },
      }
      const { error } = await authClient.signIn.magicLink(params)

      if (error) throw new Error(error.message)

      setMagicLinkParams(params)
      setMagicLinkSent(true)
      toast.success('Magic link sent! Check your email to sign in')
    } catch (error) {
      console.error('Magic link authentication failed:', error)
      if (error instanceof Error) {
        toast.error(error.message || 'Failed to send magic link')
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onSignUpSubmit = async (data: TSignUpFormValues) => {
    try {
      setIsLoading(true)
      const returnUrlPath = searchParams.get('returnUrl') || '/app'
      const returnUrl = getAbsoluteUrl(returnUrlPath)
      const name = `${data.firstName.trim()}⦀${data.lastName.trim()}`

      const params: MagicLinkParams = {
        email: data.email,
        callbackURL: returnUrl,
        name,
        fetchOptions: {
          body: { mode: 'signup' },
        },
      }
      const { error } = await authClient.signIn.magicLink(params)

      if (error) throw new Error(error.message)

      setMagicLinkParams(params)
      setMagicLinkSent(true)
      toast.success('Magic link sent! Check your email to sign up')
      signUpForm.reset()
    } catch (error) {
      console.error('Magic link authentication failed:', error)
      if (error instanceof Error) {
        toast.error(error.message || 'Failed to send magic link')
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (magicLinkSent && magicLinkParams) {
    return <ConfirmEmailAddress magicLinkParams={magicLinkParams} />
  }

  return (
    <AuthCard
      title={title}
      description={description}
      footer={
        <p className="text-center text-muted-foreground text-xs">
          {footerText}{' '}
          <Button
            variant="link"
            className="!p-0 text-xs"
            onClick={() => router.push(getUrlWithParams(footerLinkHref, searchParams))}
          >
            {footerLinkText}
          </Button>
        </p>
      }
      className={className}
    >
      {isSignUp ? (
        <form onSubmit={signUpForm.handleSubmit(onSignUpSubmit)} className="grid gap-6">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            isLoading={isGoogleLoading}
            onClick={handleGoogleSignIn}
          >
            <BrandIcons.google className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-sidebar-border border-t" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-sidebar px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="-mb-3 grid grid-cols-2 gap-2">
            <div>
              <Input
                id="firstName"
                placeholder="First name"
                disabled={isLoading}
                {...signUpForm.register('firstName')}
              />
              {signUpForm.formState.errors.firstName && (
                <p className="mt-1 text-destructive text-xs">
                  {signUpForm.formState.errors.firstName.message}
                </p>
              )}
            </div>
            <div>
              <Input
                id="lastName"
                placeholder="Last name"
                disabled={isLoading}
                {...signUpForm.register('lastName')}
              />
              {signUpForm.formState.errors.lastName && (
                <p className="mt-1 text-destructive text-xs">
                  {signUpForm.formState.errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <Input
              id="email"
              type="email"
              placeholder="Email address"
              autoCapitalize="none"
              autoComplete="email"
              disabled={isLoading}
              {...signUpForm.register('email')}
            />
            {signUpForm.formState.errors.email && (
              <p className="mt-1 text-destructive text-xs">
                {signUpForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <Button type="submit" className="-mt-1 w-full" isLoading={isLoading}>
            Create Account
          </Button>
        </form>
      ) : (
        <form onSubmit={signInForm.handleSubmit(onSignInSubmit)} className="grid gap-6">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            isLoading={isGoogleLoading}
            onClick={handleGoogleSignIn}
          >
            <BrandIcons.google className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-sidebar-border border-t" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-sidebar px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div>
            <Input
              id="email"
              type="email"
              placeholder="Email address"
              autoCapitalize="none"
              autoComplete="email"
              disabled={isLoading}
              {...signInForm.register('email')}
            />
            {signInForm.formState.errors.email && (
              <p className="mt-1 text-destructive text-xs">
                {signInForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <Button type="submit" className="-mt-1 w-full" isLoading={isLoading}>
            Send Magic Link
          </Button>
        </form>
      )}
    </AuthCard>
  )
}
